import DriveApi from "../utils/api";
import { TApiSuccessCallback, TApiErrorCallback, TApiCredentialsCallback, IApiError, TMultiCallParam } from "../utils/api.interfaces";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import Service from "../services/base";
import { ICustomHeaders, NoConnectionTransport, Transport, XHRTransport } from "../utils/transport";
import { UserService } from "../utils/users";
import { DriveStore } from "../store/reducers";
import { ICredentials } from "../utils/credentialsStore";
import IamApi from "../utils/auth/iamApi";
import { IIamSettings } from "../utils/auth/iamSettings";
import { IamService } from "./iamService";
import { IFeatureFlagsService } from "./featureFlags";
import { TContextSource } from "../app.manager";
import NewIamApi from "../utils/auth/newIamApi";
import { isGetTokenModule } from "native-sdk";
import { FeatureFlagName } from "../utils/featureFlagName";
import { shouldUseAppAuthIamLogin } from "../utils/auth/appAuthIamLogin";

const NOOP = () => {
    // nothing to do;
};

export default class Api extends Service {

    driveApi: DriveApi;
    constructor(
        private readonly userService: UserService,
        private readonly store: DriveStore,
        userName?: string,
        private readonly iamService?: IamService,
        private readonly featureFlagsService?: IFeatureFlagsService,
        private readonly source?: TContextSource
    ) {
        super();
        this.driveApi = this.getApi(userName);
    }

    getApi(userName?: string) {
        let credentials = userName && this.store.getState().driveAPICredentials && this.store.getState().driveAPICredentials[userName];
        // new IAM flow, access token is now stored on the native side
        const isAppAuthEnabled = shouldUseAppAuthIamLogin(
            this.featureFlagsService?.hasFeatureFlag(FeatureFlagName.ENABLE_APP_AUTH_IAM_LOGIN_POPUP)
        );
        if (credentials && !credentials.credentials.iamJwt && !credentials.credentials.sessionId && isGetTokenModule() && isAppAuthEnabled) {
            return new NewIamApi(this.userService, this.store, userName, this.source, this.featureFlagsService);
        }

        return credentials && credentials.iamSettings && this.iamService
                ? new IamApi(this.userService, this.store, this.source || "Api", userName, true, this.iamService, this.featureFlagsService)
                : new DriveApi(this.userService, this.store, userName, this.featureFlagsService, this.source);
    }

    call(method: string, params: any, callbackSuccess?: TApiSuccessCallback, callbackError?: TApiErrorCallback, ignoreCredentials?: boolean, binaryData?: ArrayBuffer, binaryMimeType?: string) {
        this.driveApi.call(method, params, callbackSuccess || NOOP, callbackError || NOOP, ignoreCredentials, binaryData, binaryMimeType);
    }

    callPromise(method: string, params: any, ignoreCredentials?: boolean) {
        return this.driveApi.callPromise(method, params, ignoreCredentials);
    }

    multiCall(calls: TMultiCallParam[], callbackSuccess: TApiSuccessCallback, callbackError: TApiErrorCallback) {
        this.driveApi.multiCall(calls, callbackSuccess || NOOP, callbackError || NOOP);
    }

    multiCallPromise(calls: TMultiCallParam[], ignoreCredentials = false): Promise {
        return this.driveApi.multiCallPromise(calls, ignoreCredentials);
    }

    serverCall(server: string, method: string, params: any): Promise {
        return new Promise<any[]>((resolve, reject: (error: IApiError) => void) => {
            let transport = window.DRIVE_DEBUG?.SERVERCALL_NO_CONNECTION ? new NoConnectionTransport(server) : new XHRTransport(server);
            transport.setIsSslDisabled(!!(window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_SSL));
            transport.call(method, params, resolve, reject);
        });
    }

    getSession(successCallback: TApiCredentialsCallback | null, newSession = false) {
        return this.driveApi.getSession(successCallback, newSession);
    }

    forget(successCallback?: TApiSuccessCallback, errorMessage?: string, skipNeedsCredentials = false) {
        this.driveApi.forget(successCallback, errorMessage, skipNeedsCredentials);
    }

    refresh(userName?: string | boolean) {
        this.driveApi.refresh(userName);
    }

    setUser(credentials: ICredentials, server: string, iamSettings?: IIamSettings) {
        this.driveApi.setUser(credentials, server, iamSettings);
    }

    getUser() {
        return this.driveApi.getUser();
    }

    getCurrentCredentials() {
        return this.driveApi.getCurrentCredentials();
    }

    getDriverCredentials(username: string) {
        return this.driveApi.getDriverCredentials(username);
    }

    authenticate(newServer: string, database: string, username: string, password: string, success: TApiSuccessCallback, error: TApiErrorCallback) {
        return this.driveApi.authenticate(newServer, database, username, password, success, error);
    }

    destroy() {
        this.driveApi.destroy();
    }

    setTransport(transport: Transport) {
        this.driveApi.setTransport(transport);
    }

    setHeaders(headers: ICustomHeaders[]) {
        this.driveApi.setHeaders(headers);
    }
}
